import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import { adminToolsApi } from '../services/api';

interface Task {
  task_id: string;
  task_display_id: string;
  status: string;
  sla: string;
  region_cluster: string;
  priority: number;
  due_at: string | null;
  created_at: string;
  updated_at: string;
  order_id: string;
  order_display_id: string;
  supplier_name: string;
  city: string;
  state: string;
  validation_status: string;
}

interface UserTasksDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
}

const UserTasksDialog: React.FC<UserTasksDialogProps> = ({ open, onClose, userId, userName }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unassigning, setUnassigning] = useState<string | null>(null);
  const [unassignDialogOpen, setUnassignDialogOpen] = useState(false);
  const [taskToUnassign, setTaskToUnassign] = useState<Task | null>(null);
  const [unassignReason, setUnassignReason] = useState('');

  useEffect(() => {
    if (open && userId) {
      fetchTasks();
    }
  }, [open, userId]);

  const fetchTasks = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminToolsApi.getUserTasks(userId);
      console.log('📥 Fetched tasks:', response);
      setTasks(response.tasks || []);
    } catch (err: any) {
      console.error('❌ Error fetching tasks:', err);
      setError(err.response?.data?.message || 'Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUnassignDialog = (task: Task) => {
    setTaskToUnassign(task);
    setUnassignReason('');
    setUnassignDialogOpen(true);
  };

  const handleCloseUnassignDialog = () => {
    setUnassignDialogOpen(false);
    setTaskToUnassign(null);
    setUnassignReason('');
  };

  const handleConfirmUnassign = async () => {
    if (!taskToUnassign) return;

    setUnassigning(taskToUnassign.task_id);
    setUnassignDialogOpen(false);

    try {
      await adminToolsApi.unassignTask(
        taskToUnassign.task_id,
        userId,
        unassignReason || 'Manual unassignment from CMS Roster'
      );
      console.log(`✅ Task ${taskToUnassign.task_display_id} unassigned successfully`);

      // Remove the task from the list
      setTasks(tasks.filter(t => t.task_id !== taskToUnassign.task_id));
      setTaskToUnassign(null);
      setUnassignReason('');
    } catch (err: any) {
      console.error('❌ Error unassigning task:', err);
      setError(err.response?.data?.message || 'Failed to unassign task');
    } finally {
      setUnassigning(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'ready': return 'default';
      case 'assigned': return 'primary';
      case 'accepted': return 'info';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'hold': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Tasks Assigned to {userName}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : tasks.length === 0 ? (
          <Alert severity="info">No tasks assigned to this user</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Task ID</strong></TableCell>
                  <TableCell><strong>Order ID</strong></TableCell>
                  <TableCell><strong>Supplier</strong></TableCell>
                  <TableCell><strong>Location</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>SLA</strong></TableCell>
                  <TableCell><strong>Region</strong></TableCell>
                  <TableCell><strong>Created</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((task) => (
                  <TableRow key={task.task_id}>
                    <TableCell>{task.task_display_id}</TableCell>
                    <TableCell>{task.order_display_id}</TableCell>
                    <TableCell>{task.supplier_name}</TableCell>
                    <TableCell>{task.city}, {task.state}</TableCell>
                    <TableCell>
                      <Chip
                        label={task.status}
                        color={getStatusColor(task.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{task.sla}</TableCell>
                    <TableCell>{task.region_cluster}</TableCell>
                    <TableCell>{formatDate(task.created_at)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenUnassignDialog(task)}
                        disabled={unassigning === task.task_id}
                        title="Unassign Task"
                      >
                        {unassigning === task.task_id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Typography variant="body2" color="textSecondary" sx={{ mr: 'auto' }}>
          Total: {tasks.length} task{tasks.length !== 1 ? 's' : ''}
        </Typography>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Unassign Confirmation Dialog */}
      <Dialog
        open={unassignDialogOpen}
        onClose={handleCloseUnassignDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            <Typography variant="h6">Unassign Task</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to unassign the following task?
          </DialogContentText>

          {taskToUnassign && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" color="textSecondary">
                <strong>Task ID:</strong> {taskToUnassign.task_display_id}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Order ID:</strong> {taskToUnassign.order_display_id}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Supplier:</strong> {taskToUnassign.supplier_name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Location:</strong> {taskToUnassign.city}, {taskToUnassign.state}
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason (Optional)"
            placeholder="Enter reason for unassigning this task..."
            value={unassignReason}
            onChange={(e) => setUnassignReason(e.target.value)}
            variant="outlined"
          />

          <DialogContentText sx={{ mt: 2, fontSize: '0.875rem' }}>
            This will set the task status to <strong>"ready"</strong> and remove the user assignment.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUnassignDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUnassign}
            color="error"
            variant="contained"
            autoFocus
          >
            Unassign Task
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default UserTasksDialog;

